import { logger } from "@/src/utils/logger";
import { useEffect, useState } from "react";

/**
 * Hook for experiment variant overrides using localStorage.
 * Allows developers to override PostHog feature flag variants locally.
 * Only works in non-production environments.
 */
export const useExperimentOverride = (
  experimentName: string,
  posthogVariant: string | boolean | undefined
): {
  effectiveVariant: string | null;
  setOverrideVariant: (variant: string | null) => void;
  clearOverrideVariant: () => void;
  hasOverride: boolean;
  isInitialized: boolean;
} => {
  const [overrideVariant, setOverrideVariantState] = useState<string | null>(
    null
  );
  const [isInitialized, setIsInitialized] = useState(false);

  const storageKey = `dev_variant_${experimentName}`;

  // Initialize from localStorage
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      process.env.NEXT_PUBLIC_VERCEL_ENV === "production"
    ) {
      setIsInitialized(true);
      return;
    }

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored && stored !== "null") {
        setOverrideVariantState(stored);
        logger.log(
          `🧪 [${experimentName.toUpperCase()}] Override loaded:`,
          stored
        );
      }
    } catch (error) {
      logger.error("Failed to load experiment override:", error);
    }
    setIsInitialized(true);
  }, [experimentName, storageKey]);

  const setOverrideVariant = (variant: string | null) => {
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") {
      logger.warn("Experiment overrides are disabled in production");
      return;
    }

    setOverrideVariantState(variant);

    try {
      if (variant === null) {
        localStorage.removeItem(storageKey);
        logger.log(`🧪 [${experimentName.toUpperCase()}] Override cleared`);
      } else {
        localStorage.setItem(storageKey, variant);
        logger.log(
          `🧪 [${experimentName.toUpperCase()}] Override set:`,
          variant
        );
      }
    } catch (error) {
      logger.error("Failed to save experiment override:", error);
    }
  };

  const clearOverrideVariant = () => {
    setOverrideVariant(null);
  };

  // Determine the effective variant
  const hasOverride = overrideVariant !== null;
  let effectiveVariant: string | null = null;

  if (hasOverride) {
    effectiveVariant = overrideVariant;
  } else if (typeof posthogVariant === "string") {
    effectiveVariant = posthogVariant;
  } else if (typeof posthogVariant === "boolean") {
    // Handle boolean feature flags (true/false)
    effectiveVariant = posthogVariant ? "enabled" : "disabled";
  }

  // Log the effective variant when it changes (only after initialization)
  useEffect(() => {
    if (isInitialized && effectiveVariant) {
      const source = hasOverride ? "override" : "PostHog";
      logger.log(
        `🧪 [${experimentName.toUpperCase()}] Effective variant: ${effectiveVariant} (${source})`
      );
    }
  }, [effectiveVariant, hasOverride, experimentName, isInitialized]);

  return {
    effectiveVariant,
    setOverrideVariant,
    clearOverrideVariant,
    hasOverride,
    isInitialized,
  };
};
